import { setProperty } from 'dot-prop';

import { type Action, ActionTypes, type State } from '@/components/Store.d';
import { log } from '@/lib/log';
import { isObject, isString, persistState } from '@/lib/util';

/**
 *
 * @param {State} state
 * @param {Action} action
 * @return {State}
 */
const Reducer = (state: State, action: Action): State => {
  let newState;

  switch (action.type) {
    case ActionTypes.isLoggedIn:
      newState = {
        ...state,
        isLoggedIn: action.payload
      };
      break;

    case ActionTypes.toggleVoucherInput:
      newState = {
        ...state,
        showVoucherInput: action.payload
      };
      break;

    case ActionTypes.showError:
      newState = {
        ...state,
        hideAlert: false,
        alertTitle: typeof action.payload === 'object' && action.payload.title ? action.payload.title : '',
        alertMessage: typeof action.payload === 'object' && action.payload.message ? action.payload.message : isString(action.payload) ? action.payload : '',
        disableOnSubmit: false
      };
      break;

    case ActionTypes.hideError:
      newState = {
        ...state,
        hideAlert: true,
        alertTitle: '',
        alertMessage: ''
      };
      break;

    case ActionTypes.showProductOnRight:
      newState = {
        ...state,
        showProductOnRight: action.payload
      };
      break;

    case ActionTypes.setProductClass:
      newState = {
        ...state,
        productClass: action.payload
      };
      break;

    case ActionTypes.setPricingText:
      newState = {
        ...state,
        pricingText: action.payload
      };
      break;

    case ActionTypes.setVoucher:
      newState = {
        ...state,
        voucher: action.payload
      };
      break;

    case ActionTypes.setFreePurchase:
      newState = {
        ...state,
        isFreePurchase: action.payload
      };
      break;

    case ActionTypes.removeVoucher:
      newState = {
        ...state,
        showVoucherInput: false,
        voucher: undefined
      };
      break;

    case ActionTypes.setMovableSubscription:
      newState = {
        ...state,
        upgrade: action.payload
      };
      break;

    case ActionTypes.setReusePaymentMethod:
      newState = {
        ...state,
        payment: {
          ...state.payment,
          reusePaymentMethod: action.payload
        }
      };
      break;

    case ActionTypes.setPaymentMethod:
      newState = {
        ...state,
        payment: {
          ...state.payment,
          paymentMethod: action.payload
        }
      };
      break;

    case ActionTypes.addressChange:
    case ActionTypes.addressVerification:
    case ActionTypes.bundleData:
    case ActionTypes.challengeRequired:
    case ActionTypes.credentialsChange:
    case ActionTypes.disableForm:
    case ActionTypes.enableForm:
    case ActionTypes.printAccountNumberChange:
    case ActionTypes.registerLegal:
    case ActionTypes.resetCompletions:
    case ActionTypes.setCurrentPage:
    case ActionTypes.setChallengeV2Data:
    case ActionTypes.setEntryPage:
    case ActionTypes.setStreetId:
    case ActionTypes.setTokenId:
    case ActionTypes.supporterChangeTier:
    case ActionTypes.supporterCustomMinimumAmount:
      newState = {
        ...state,
        ...setProperty({ ...state }, action.payload.key, action.payload.value)
      };
      break;

    case ActionTypes.initSupporter:
      newState = {
        ...state,
        supporter: {
          minimumAmount: action.payload.minimumAmount,
          selection: {
            period: action.payload.period,
            tier: action.payload.tier
          },
          tiers: action.payload.tiers
        }
      };
      break;

    case ActionTypes.setPhoneNumber:
      newState = {
        ...state,
        ...setProperty({ ...state }, 'user.phone.number', action.payload.number),
        ...setProperty({ ...state }, 'user.phone.countryCode', action.payload.countryCode)
      };
      break;

    case ActionTypes.setEmail:
      newState = {
        ...state,
        user: {
          ...state.user,
          email: action.payload
        }
      };
      break;

    case ActionTypes.zipCity:
      newState = {
        ...state,
        ...setProperty({ ...state }, 'address.delivery.city', action.payload.city),
        ...setProperty({ ...state }, 'address.delivery.zip', action.payload.zip)
      };
      break;

    case ActionTypes.purchaseCompleted:
      newState = {
        ...state,
        order: action.payload
      };
      break;

    case ActionTypes.setMppCredentials:
      newState = {
        ...state,
        payment: {
          sessionToken: action.payload.sessionToken,
          accountReference: action.payload.accountReference
        },
        user: {
          ...state.user,
          ip: action.payload.clientIp
        }
      };
      break;

    case ActionTypes.supporterChangePeriod:
      newState = {
        ...state,
        supporter: {
          selection: {
            period: action.payload.selection
          },
          tiers: action.payload.tiers
        }
      };
      break;

    case ActionTypes.restoreFromStorage:
      newState = {
        ...action.payload,
        disableOnSubmit: false,
        hideAlert: true,
        restoredFromStorage: true
      };
      break;

    case ActionTypes.hideDialog:
      newState = {
        ...state,
        dialog: {
          isHidden: true,
          title: '',
          body: ''
        }
      };
      break;

    case ActionTypes.showDialog:
      newState = {
        ...state,
        dialog: {
          isHidden: false,
          type: action.payload.type,
          title: isObject(action.payload) && action.payload.title ? action.payload.title : '',
          body: isObject(action.payload) && action.payload.body ? action.payload.body : isString(action.payload) ? action.payload : '',
          actions: action.payload.actions
        },
        disableForm: false
      };
      break;

    case ActionTypes.setEligibleForTrial:
      newState = {
        ...state,
        eligibleForTrial: action.payload
      };
      break;

    default:
      return state;
  }

  log(`Reducer "${action.type}"`, {
    payload: 'payload' in action ? action.payload : undefined,
    newState: newState
  });

  persistState(newState as State);

  return newState as State;
};

export default Reducer;
